<template>
  <div class="payment-success">
    <div class="success-container">
      <div class="content-wrapper">
        <div v-if="loading" class="status-container">
          <div class="status-mark spinner">
            <span class="material-icons-outlined">progress_activity</span>
          </div>
          <p>{{ $t('loading') }}</p>
        </div>

        <div v-else-if="error" class="status-container">
          <div class="status-mark error-icon">
            <span class="material-icons-outlined">error</span>
          </div>
          <p>{{ error }}</p>
          <button @click="verifyPayment" class="btn btn-primary">
            {{ $t('retry') }}
          </button>
        </div>

        <div v-else class="success-content">
          <div class="status-mark success-icon">
            <span class="material-icons-outlined">check_circle</span>
          </div>
          
          <h1>{{ $t('paymentSuccess.title') }}</h1>
          <h2>{{ successSubtitle }}</h2>
          <p class="description">{{ successDescription }}</p>

          <div class="membership-details" v-if="membership">
            <h3>{{ $t('membership.details') }}</h3>
            <div class="details-grid">
              <div class="detail-item">
                <span class="label">{{ $t('membership.plan') }}</span>
                <span class="value">{{ getPlanName }}</span>
              </div>
              <div class="detail-item">
                <span class="label">{{ $t('membership.startDate') }}</span>
                <span class="value">{{ formatDate(membership.startDate) }}</span>
              </div>
              <div class="detail-item">
                <span class="label">{{ $t('membership.endDate') }}</span>
                <span class="value">{{ formatDate(membership.endDate) }}</span>
              </div>
              <div class="detail-item">
                <span class="label">{{ $t('membership.type') }}</span>
                <span class="value">{{ membership.isSubscription ? $t('membership.autoRenewal') : $t('membership.oneTime') }}</span>
              </div>
            </div>
          </div>

          <div class="membership-details" v-if="credits">
            <h3>{{ $t('paymentSuccess.creditPurchase.details') }}</h3>
            <div class="details-grid">
              <div class="detail-item">
                <span class="label">{{ $t('paymentSuccess.creditPurchase.added') }}</span>
                <span class="value">{{ formatNumber(credits.amount) }}</span>
              </div>
              <div class="detail-item">
                <span class="label">{{ $t('paymentSuccess.creditPurchase.permanentBalance') }}</span>
                <span class="value">{{ formatNumber(credits.remaining) }}</span>
              </div>
              <div class="detail-item">
                <span class="label">{{ $t('paymentSuccess.creditPurchase.access') }}</span>
                <span class="value">{{ $t('paymentSuccess.creditPurchase.proAccess') }}</span>
              </div>
              <div class="detail-item">
                <span class="label">{{ $t('paymentSuccess.creditPurchase.expiry') }}</span>
                <span class="value">{{ $t('paymentSuccess.creditPurchase.never') }}</span>
              </div>
            </div>
          </div>

          <div class="buttons">
            <router-link to="/" class="btn btn-primary">
              {{ $t('paymentSuccess.buttons.backToHome') }}
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { auth } from '@/firebase'
import { onAuthStateChanged } from 'firebase/auth'
import { apiPost } from '@/composables/useApi'

export default {
  name: 'PaymentSuccess',
  data() {
    return {
      membership: null,
      credits: null,
      loading: true,
      error: null
    }
  },
  computed: {
    successSubtitle() {
      if (this.credits) {
        return this.$t('paymentSuccess.creditPurchase.subtitle')
      }

      const planNames = {
        pro: 'Pro',
        premium: 'Premium'
      }
      const planName = planNames[this.membership?.planType]

      return planName
        ? this.$t('paymentSuccess.subtitle', { plan: planName })
        : this.$t('paymentSuccess.subscriptionConfirmed')
    },
    successDescription() {
      return this.$t(
        this.credits
          ? 'paymentSuccess.creditPurchase.description'
          : 'paymentSuccess.description'
      )
    },
    getPlanName() {
      if (!this.membership?.plan) return this.$t('membership.unknown')
      const planParts = this.membership.plan.split('_')
      const period = planParts[0] // monthly or yearly
      const type = planParts[1] // oneTime or autoRenewal
      const planType = this.membership.planType
      return `${this.$t(`membership.planTypes.${planType}`)} ${this.$t(`membership.planTypes.${period}`)}`
    }
  },
  methods: {
    formatNumber(value) {
      return Number(value || 0).toLocaleString()
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString(this.$i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    async verifyPayment() {
      this.loading = true
      this.error = null
      
      try {
        const sessionId = this.$route.query.session_id
        if (!sessionId) {
          throw new Error(this.$t('paymentSuccess.errors.noSession'))
        }

        const user = await new Promise((resolve) => {
          const unsubscribe = onAuthStateChanged(auth, (user) => {
            unsubscribe()
            resolve(user)
          })
        })

        if (!user) {
          throw new Error(this.$t('paymentSuccess.errors.notLoggedIn'))
        }

        const data = await apiPost('/api/verify-payment', { sessionId })
        this.membership = data.membership
        this.credits = data.credits
      } catch (error) {
        this.error = error.message
      } finally {
        this.loading = false
      }
    }
  },
  async mounted() {
    await this.verifyPayment()
  }
}
</script>

<style scoped>
.payment-success {
  margin-top: var(--navbar-height);
  width: 100%;
  min-height: calc(100vh - var(--navbar-height));
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.7), rgba(246, 248, 251, 0.96)),
    var(--color-page);
  padding: 2rem;
}

.success-container {
  background: var(--workspace-shell-bg);
  border: 1px solid var(--workspace-shell-border);
  border-radius: var(--radius-xl);
  width: 100%;
  max-width: 640px;
  box-shadow: var(--workspace-shell-shadow);
}

.content-wrapper {
  padding: 3rem;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.status-container {
  text-align: center;
  width: 100%;
}

.status-mark {
  width: 4rem;
  height: 4rem;
  margin: 0 auto 1.5rem;
  border-radius: var(--radius-xl);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--color-surface-muted);
}

.status-mark .material-icons-outlined {
  font-size: 2.65rem;
}

.spinner, .error-icon, .success-icon {
  margin-bottom: 1.5rem;
}

.spinner {
  color: var(--color-brand-600);
  background: var(--color-brand-50);
}

.spinner .material-icons-outlined {
  animation: spin 1s linear infinite;
}

.error-icon {
  color: var(--color-danger);
  background: var(--color-danger-bg);
}

.success-icon {
  color: var(--color-success);
  background: var(--color-success-bg);
  animation: scale-in 0.5s ease-out;
}

.success-content {
  width: 100%;
  text-align: center;
}

h1 {
  color: var(--color-text);
  font-size: clamp(2rem, 5vw, 2.5rem);
  margin-bottom: 0.7rem;
  font-weight: 800;
  letter-spacing: -0.04em;
}

h2 {
  color: var(--color-text-muted);
  font-size: 1.25rem;
  margin-bottom: 1.25rem;
  font-weight: 500;
}

.description {
  color: var(--color-text-subtle);
  font-size: 1.1rem;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.membership-details {
  background: var(--color-surface-muted);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  padding: 1.25rem;
  margin: 2rem 0;
  text-align: left;
}

.membership-details h3 {
  color: var(--color-text);
  font-size: 1.25rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.detail-item .label {
  color: var(--color-text-subtle);
  font-size: 0.9rem;
  font-weight: 500;
}

.detail-item .value {
  color: var(--color-text);
  font-size: 1.1rem;
  font-weight: 600;
}

.buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
}

.btn {
  min-height: 44px;
  padding: 0 1.35rem;
  border-radius: var(--radius-pill);
  font-size: 0.95rem;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.2s;
}

.btn-primary {
  background: var(--color-text);
  color: var(--color-text-inverse);
  border: none;
}

.btn-primary:hover {
  background: var(--color-brand-600);
  transform: translateY(-1px);
}

.btn-outline {
  background: transparent;
  color: var(--color-success);
  border: 2px solid var(--color-success);
}

.btn-outline:hover {
  background: var(--color-success);
  color: var(--color-text-inverse);
  transform: translateY(-2px);
}

@keyframes scale-in {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 640px) {
  .content-wrapper {
    padding: 2rem;
    min-height: 300px;
  }

  .buttons {
    flex-direction: column;
  }

  h1 {
    font-size: 2rem;
  }

  h2 {
    font-size: 1.25rem;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }
}
</style>
